import { FunctionComponent, useMemo } from 'react';
import { useQuery } from 'react-query';
import { useTranslation } from 'react-i18next';
import { AxiosError } from 'axios';
import { CoUser } from 'entities/CoUser/types';
import { getUserLogs } from 'entities/Logs/logsApi';
import { showErrorNotification } from 'shared/helpers/showErrorNotification';
import { useCustomSearchParams } from 'shared/hooks/use-custom-search';
import { EmptyTable } from 'shared/ui/EmptyTable';
import { TableWithHeader } from 'shared/ui/tables/TableWithHeader';
import { UserLogRow, UserLogsColumns } from './userLogsColumns';

interface Props {
	coUser: CoUser | null;
}

export const Logs: FunctionComponent<Props> = ({ coUser }) => {
	const { t } = useTranslation();
	const { searchQueries, changeSearchQueries } = useCustomSearchParams();
	const userId = coUser?.userId?._id;

	const { data } = useQuery({
		queryKey: ['logs/user', userId, searchQueries],
		queryFn: () => getUserLogs(userId as string, searchQueries),
		enabled: !!userId,
		keepPreviousData: true,
		onError: (err: AxiosError) => showErrorNotification(err),
	});

	const rows: UserLogRow[] = useMemo(
		() => data?.data.results.map((log) => ({ ...log, id: log._id })) ?? [],
		[data],
	);

	return rows.length ? (
		<TableWithHeader
			columns={UserLogsColumns}
			rows={rows}
			pagination={{
				page: searchQueries.page,
				totalCount: data?.data.totalCount || 0,
				perPage: data?.data.perPage || 0,
				onPageChange: (page) => changeSearchQueries({ page }),
			}}
		/>
	) : (
		<EmptyTable text={t('shared_texts.not_found')} />
	);
};
